import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { ModalDirective } from 'ng-uikit-pro-standard';
import { Subscription } from 'rxjs';
import { YellowBrickRoadService } from '../../layouts/application-layout/yellow-brick-road/yellow-brick-road.service';
import { nullSafePath } from '../../shared/helpers/null-safe-path';
import { FeatureFlagService } from '../../shared/services/feature-flag.service';
import { SessionService } from '../../shared/services/session.service';
import { ApplicantService } from '../applicant-service/applicant.service';
import { Answer } from '../applicant-service/service-models/Answer';
import { IterativeApplicationRouterService } from '../iterative-application-router/iterative-application-router.service';
import { JobService } from '../job-service/job.service';
import { JobStatusModel } from '../job-service/models/job-status-model';
import { Question } from '../job-service/service-models/Question';
import { ReviewModel } from '../job-service/service-models/ReviewModel';
import { JobStatusService } from '../job-status.service';

@Component({
  selector: 'app-review-and-submit',
  templateUrl: './review-and-submit.component.html',
  styleUrls: ['./review-and-submit.component.scss']
})
export class ReviewAndSubmitComponent implements OnInit {
  @ViewChild('cancelModal')
  cancelModal: ModalDirective;
  applicantId: string;
  identifier: string;
  subBrandIdentifier: string;
  jobId: number;
  readAndConcurCertification: boolean;
  model: ReviewModel;
  subscription = new Subscription();
  shouldCollectEEOCForm: boolean;
  processingServerSideRequest = false;
  applicantTextConsentQuestion: Question;
  applicantCallConsentQuestion: Question;
  certificationQuestion: Question;
  firstNameQuestion: Question;
  lastNameQuestion: Question;
  applicantCallConsentFlagEnabled: boolean;

  constructor(private route: ActivatedRoute,
    private router: Router,
    private applicantService: ApplicantService,
    private jobService: JobService,
    private jobStatusService: JobStatusService,
    private readonly sessionService: SessionService,
    private readonly ybrService: YellowBrickRoadService,
    private readonly featureFlagService: FeatureFlagService,
    private readonly iterativeApplicationServiceRouter: IterativeApplicationRouterService) {
    this.subscription.add(
      this.ybrService.modelUpdated$.subscribe(
        ybrModel => {
          this.shouldCollectEEOCForm = ybrModel.eeocForm.isIncluded;
        }
      ));
  }

  ngOnInit() {
    if (this.featureFlagService.flags !== undefined) {
      if (this.featureFlagService.flags['applicant-call-consent'] !== undefined) {
        this.applicantCallConsentFlagEnabled = this.featureFlagService.flags['applicant-call-consent'];
      }
    }
    this.featureFlagService.flagChange.subscribe((flags) => {
      this.applicantCallConsentFlagEnabled = flags['applicant-call-consent'];
    });

    this.subscription.add(this.route.parent.params.subscribe((params: Params) => {
      this.identifier = params['identifier'];
      this.subBrandIdentifier = params['subBrandIdentifier'];
      this.jobId = params['jobId'];
      this.applicantId = params['applicantId'];
    }));

    this.subscription.add(this.route.data
      .subscribe((data: { model: ReviewModel, status: JobStatusModel }) => {
        this.model = data.model;
        this.jobStatusService.checkIfPageAccessible(data.status, true);
        this.readAndConcurCertification = false;

        this.applicantCallConsentQuestion = this.model.certifications.questions.find(x => x.type === 'YesNo'
          && x.identifier === 'applicant-call-consent');
        this.applicantTextConsentQuestion = this.model.certifications.questions.find(x => x.type === 'YesNo'
          && x.identifier === 'applicant-text-consent');
        this.certificationQuestion = this.model.certifications.questions.find(x => x.type === 'MultipleChoice');
        this.firstNameQuestion = this.model.certifications.questions.find(x => x.type === 'Text' && x.identifier === 'first-name');
        this.lastNameQuestion = this.model.certifications.questions.find(x => x.type === 'Text' && x.identifier === 'last-name');

        if (this.firstNameQuestion) {
          const firstNameAnswer = new Answer();
          firstNameAnswer.questionId = this.firstNameQuestion.id;
          firstNameAnswer.questionVersion = this.firstNameQuestion.version;
          this.firstNameQuestion.answers = new Array<Answer>();
          this.firstNameQuestion.answers.push(firstNameAnswer);

          const lastNameAnswer = new Answer();
          lastNameAnswer.questionId = this.lastNameQuestion.id;
          lastNameAnswer.questionVersion = this.lastNameQuestion.version;
          this.lastNameQuestion.answers = new Array<Answer>();
          this.lastNameQuestion.answers.push(lastNameAnswer);
        }
      })
    );
  }

  displayCancelModal() {
    this.cancelModal.show();
  }

  cancel() {
    this.router.navigate([this.identifier]);
  }

  private getBasePath() {
    return nullSafePath(this.identifier, this.subBrandIdentifier, 'jobs', this.jobId, 'apply', this.applicantId);
  }

  back() {
    if (this.shouldCollectEEOCForm) {
      this.router.navigate([...this.getBasePath(), 'voluntary-identification']);
    } else if (this.model.yellowBrickRoad.displayProfile) {
      this.router.navigate([...this.getBasePath(), 'personal-information']);
    } else {
      this.iterativeApplicationServiceRouter.back(
        nullSafePath(this.identifier, this.subBrandIdentifier),
        this.model.yellowBrickRoad.orderableElements,
        0,
        this.jobId,
        this.applicantId
      );
    }
  }

  save() {
    if (this.processingServerSideRequest) {
      return;
    }

    this.processingServerSideRequest = true;

    const applicationId = localStorage.getItem('applicationId');
    const completeApplicationPartRequestId = sessionStorage.getItem('completeApplicationPartRequestId');

    let answers = this.certificationQuestion.answers;
    if (this.model.applicantTextEnabled && this.applicantTextConsentQuestion !== undefined) {
      answers = answers.concat(this.applicantTextConsentQuestion.answers);
    }
    if (this.applicantCallConsentQuestion !== undefined) {
      answers = answers.concat(this.applicantCallConsentQuestion.answers);
    }

    if (this.firstNameQuestion) {
      answers = answers.concat(this.firstNameQuestion.answers);
      answers = answers.concat(this.lastNameQuestion.answers);
    }

    if (completeApplicationPartRequestId !== undefined && completeApplicationPartRequestId !== null) {
      this.subscription.add(
        this.applicantService.submitCompleteIterativeApplicationPartRequest(
            applicationId,
            answers,
            +completeApplicationPartRequestId)
          .subscribe(
            data => {
              this.router.navigate([...this.getBasePath(), 'thank-you']);
              this.processingServerSideRequest = false;
            },
            error => {
              this.processingServerSideRequest = false;
            }));
    } else {
      this.subscription.add(
        this.applicantService.submitApplication(applicationId, answers)
          .subscribe(
            data => {
              this.router.navigate([...this.getBasePath(), 'thank-you']);
              this.processingServerSideRequest = false;
            },
            error => {
              this.processingServerSideRequest = false;
              if (error.error.JOB_CLOSED || error.error.JOB_PAUSED) {
                this.router.navigate([this.identifier, 'jobs', 'job-closed']);
              }
            }));
    }
  }

  canSubmit() {
    const applicantTextHasAnswers = this.applicantTextConsentQuestion === undefined
      || (this.applicantTextConsentQuestion.answers !== undefined &&
          this.applicantTextConsentQuestion.answers.length > 0 &&
          this.applicantTextConsentQuestion.answers[0].value);
    const applicantCallHasAnswers = this.applicantCallConsentQuestion === undefined
      || (this.applicantCallConsentQuestion.answers !== undefined &&
          this.applicantCallConsentQuestion.answers.length > 0 &&
          this.applicantCallConsentQuestion.answers[0].value);
    
    const certificationHasAnswers = this.certificationQuestion === undefined
      || (this.certificationQuestion.answers !== undefined && this.certificationQuestion.answers.length);

    const correctFirstNameAnswer = this.firstNameQuestion === undefined
      || (this.firstNameQuestion !== undefined
        && this.firstNameQuestion.answers !== undefined
        && this.firstNameQuestion.answers.length > 0
        && this.firstNameQuestion.answers[0].value === this.model.applicantName.firstName);
    const correctLastNameAnswer = this.lastNameQuestion === undefined
      || (this.lastNameQuestion !== undefined
        && this.lastNameQuestion.answers !== undefined
        && this.lastNameQuestion.answers.length > 0
        && this.lastNameQuestion.answers[0].value === this.model.applicantName.lastName);

    if (this.model.applicantTextEnabled) {
      return applicantTextHasAnswers && certificationHasAnswers && correctFirstNameAnswer && correctLastNameAnswer &&
        (!this.applicantCallConsentFlagEnabled || (this.applicantCallConsentFlagEnabled && applicantCallHasAnswers));
    } else {
      return certificationHasAnswers && correctFirstNameAnswer && correctLastNameAnswer &&
        (!this.applicantCallConsentFlagEnabled || (this.applicantCallConsentFlagEnabled && applicantCallHasAnswers));
    }
  }

  goToJobsPage() {
    this.router.navigate([
      location.pathname.split('/')[1]
    ]);
  }
}
